import { useRef, useState } from "react";
import {
  humanDuration,
  looksLikeUrl,
  mmss,
  parseYouTubePlaylistId,
  parseYouTubeVideoId,
  type Playlist,
  type PlaylistSummary,
  type SearchResult,
} from "radio-shared";
import { api } from "../api";

/**
 * Coller un lien, chercher un son ou une playlist, ou déposer un fichier.
 *
 * Un seul champ : coller et chercher sont la même intention — mettre ce son-là
 * dans la file — et personne n'a envie de choisir un mode avant de savoir ce
 * qu'il tape. `looksLikeUrl` aiguille.
 *
 * Une playlist ne s'ajoute jamais sans être montrée d'abord : vingt-cinq sons
 * d'un coup, c'est beaucoup d'antenne pour une seule personne.
 */

/** Ce qu'on sait d'un résultat depuis qu'on a cliqué dessus. */
type ResultState = { status: "adding" | "queued" } | { status: "refused"; error: string };

/** La playlist ouverte, avec de quoi la réenfiler. */
type OpenPlaylist = Playlist & { playlistId: string };

const totalDuration = (results: readonly SearchResult[]) =>
  humanDuration(results.reduce((sum, r) => sum + r.durationMs, 0));

export function AddSound() {
  const [query, setQuery] = useState("");
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [mode, setMode] = useState<"videos" | "playlists">("videos");
  const [results, setResults] = useState<SearchResult[] | null>(null);
  const [playlists, setPlaylists] = useState<PlaylistSummary[] | null>(null);
  const [playlist, setPlaylist] = useState<OpenPlaylist | null>(null);
  const [states, setStates] = useState<Record<string, ResultState>>({});
  /** Le lien collé appartenait à une playlist : on la propose, sans l'imposer. */
  const [offer, setOffer] = useState<string | null>(null);
  const fileInput = useRef<HTMLInputElement>(null);

  const clearLists = () => {
    setResults(null);
    setPlaylists(null);
    setPlaylist(null);
    setStates({});
  };

  const run = async (action: () => Promise<unknown>, ok: string) => {
    setBusy(true);
    setMessage(null);
    try {
      await action();
      setMessage(ok);
      setQuery("");
    } catch (err) {
      setMessage(err instanceof Error ? err.message : "Raté");
    } finally {
      setBusy(false);
    }
  };

  const search = async (q: string, which: "videos" | "playlists") => {
    setBusy(true);
    setMessage(null);
    clearLists();
    setMode(which);
    try {
      if (which === "videos") {
        const { results: found } = await api.searchYouTube(q);
        setResults(found);
        // Une liste vide n'est pas une panne : la radio écarte les directs et ce
        // qui ne s'intègre pas, et il arrive qu'il ne reste rien.
        if (found.length === 0) setMessage("Rien que la radio puisse jouer — les directs sont écartés.");
      } else {
        const { playlists: found } = await api.searchPlaylists(q);
        setPlaylists(found);
        if (found.length === 0) setMessage("Aucune playlist pour cette recherche.");
      }
    } catch (err) {
      setMessage(err instanceof Error ? err.message : "Raté");
    } finally {
      setBusy(false);
    }
  };

  const openPlaylist = async (playlistId: string) => {
    setBusy(true);
    setMessage(null);
    setOffer(null);
    try {
      const opened = await api.openPlaylist(playlistId);
      setResults(null);
      setPlaylists(null);
      setStates({});
      setPlaylist({ ...opened, playlistId });
    } catch (err) {
      setMessage(err instanceof Error ? err.message : "Raté");
    } finally {
      setBusy(false);
    }
  };

  /**
   * Enfile un son en rejouant exactement le chemin d'un lien collé : c'est le
   * serveur, et lui seul, qui décide si le son peut passer à l'antenne. On garde
   * la liste affichée — on enchaîne souvent deux ou trois sons.
   */
  const enqueueOne = async (result: SearchResult) => {
    setStates((s) => ({ ...s, [result.videoId]: { status: "adding" } }));
    try {
      await api.addTrack(`https://www.youtube.com/watch?v=${result.videoId}`);
      setStates((s) => ({ ...s, [result.videoId]: { status: "queued" } }));
    } catch (err) {
      setStates((s) => ({
        ...s,
        [result.videoId]: { status: "refused", error: err instanceof Error ? err.message : "Refusé" },
      }));
    }
  };

  /** Tout ce qui n'est pas déjà parti, en une seule fois. */
  const enqueueAll = async (open: OpenPlaylist) => {
    const pending = open.results.filter((r) => states[r.videoId]?.status !== "queued");
    if (pending.length === 0) return;

    setBusy(true);
    setMessage(null);
    try {
      const { added, skipped } = await api.addTracks(
        open.playlistId,
        pending.map((r) => r.videoId),
      );
      setStates((s) => {
        const next = { ...s };
        for (const r of pending) next[r.videoId] = { status: "queued" };
        return next;
      });
      setMessage(
        skipped > 0
          ? `${added} sons dans la file — ${skipped} écartés`
          : `${added} sons dans la file`,
      );
    } catch (err) {
      setMessage(err instanceof Error ? err.message : "Raté");
    } finally {
      setBusy(false);
    }
  };

  const submit = () => {
    const value = query.trim();
    if (!value) return;
    setOffer(null);

    if (!looksLikeUrl(value)) {
      void search(value, mode);
      return;
    }

    const playlistId = parseYouTubePlaylistId(value);
    const videoId = parseYouTubeVideoId(value);

    // Une URL de playlist pure : on la montre. Un lien de vidéo, même accroché à
    // une playlist, reste un lien de vidéo — c'est la forme d'un partage YouTube
    // Music, et l'ajouter en entier surprendrait tout le monde.
    if (playlistId && !videoId) {
      clearLists();
      void openPlaylist(playlistId);
      return;
    }

    void run(() => api.addTrack(value), "Dans la file").then(() => {
      if (playlistId) setOffer(playlistId);
    });
  };

  const showTabs = (results !== null || playlists !== null) && !playlist;

  return (
    <section className="add">
      <form
        className="add__row"
        onSubmit={(e) => {
          e.preventDefault();
          submit();
        }}
      >
        <input
          className="add__input"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Colle un lien ou cherche sur YouTube"
          disabled={busy}
        />
        <button className="btn btn--primary" type="submit" disabled={busy || !query.trim()}>
          {busy ? "…" : looksLikeUrl(query.trim()) ? "Ajouter" : "Chercher"}
        </button>
      </form>

      <button className="add__file" type="button" onClick={() => fileInput.current?.click()} disabled={busy}>
        ou dépose un fichier audio
      </button>
      <input
        ref={fileInput}
        type="file"
        accept="audio/*"
        hidden
        onChange={(e) => {
          const file = e.target.files?.[0];
          e.target.value = "";
          if (file) void run(() => api.upload(file), `« ${file.name} » dans la file`);
        }}
      />

      {message && <p className="add__message">{message}</p>}

      {offer && (
        <button className="add__file" type="button" onClick={() => void openPlaylist(offer)} disabled={busy}>
          ce lien vient d'une playlist — voir ce qu'elle contient ?
        </button>
      )}

      {showTabs && (
        <div className="results__tabs" role="group" aria-label="Nature des résultats">
          {(["videos", "playlists"] as const).map((which) => (
            <button
              key={which}
              type="button"
              className={`btn btn--ghost${mode === which ? " results__tab--on" : ""}`}
              disabled={busy || mode === which}
              onClick={() => {
                const value = query.trim();
                if (value && !looksLikeUrl(value)) void search(value, which);
                else setMode(which);
              }}
            >
              {which === "videos" ? "Sons" : "Playlists"}
            </button>
          ))}
        </div>
      )}

      {playlist && (
        <div className="results__header">
          <div className="results__body">
            <p className="results__title">{playlist.title ?? "Playlist"}</p>
            <p className="results__meta">
              {playlist.results.length} son{playlist.results.length > 1 ? "s" : ""}
              {playlist.totalCount && playlist.totalCount > playlist.results.length
                ? ` sur ${playlist.totalCount}`
                : ""}{" "}
              · {totalDuration(playlist.results)}
            </p>
          </div>
          <button
            className="btn btn--primary results__add"
            type="button"
            disabled={busy}
            onClick={() => void enqueueAll(playlist)}
          >
            + Tout ajouter
          </button>
        </div>
      )}

      {playlists && playlists.length > 0 && (
        <ul className="results">
          {playlists.map((p) => (
            <li key={p.playlistId} className="results__item">
              {p.thumbUrl ? (
                <img className="results__thumb" src={p.thumbUrl} alt="" loading="lazy" />
              ) : (
                <span className="results__thumb results__thumb--empty" aria-hidden="true">
                  ☰
                </span>
              )}
              <div className="results__body">
                <p className="results__title">{p.title}</p>
                {p.uploader && <p className="results__meta">{p.uploader}</p>}
              </div>
              <button
                className="btn btn--ghost results__add"
                type="button"
                disabled={busy}
                onClick={() => void openPlaylist(p.playlistId)}
              >
                Ouvrir
              </button>
            </li>
          ))}
        </ul>
      )}

      {(results ?? playlist?.results ?? []).length > 0 && (
        <ul className="results">
          {(results ?? playlist?.results ?? []).map((result) => {
            const state = states[result.videoId];
            return (
              <li
                key={result.videoId}
                className={`results__item${state?.status === "refused" ? " results__item--refused" : ""}`}
              >
                {result.thumbUrl ? (
                  <img className="results__thumb" src={result.thumbUrl} alt="" loading="lazy" />
                ) : (
                  <span className="results__thumb results__thumb--empty" aria-hidden="true">
                    ♪
                  </span>
                )}

                <div className="results__body">
                  <p className="results__title">{result.title}</p>
                  <p className="results__meta">
                    {result.uploader ? `${result.uploader} · ` : ""}
                    {mmss(result.durationMs)}
                  </p>
                  {state?.status === "refused" && <p className="results__error">{state.error}</p>}
                </div>

                <button
                  className="btn btn--ghost results__add"
                  type="button"
                  onClick={() => void enqueueOne(result)}
                  disabled={state?.status === "adding" || state?.status === "queued"}
                >
                  {state?.status === "queued" ? "en file" : state?.status === "adding" ? "…" : "+ File"}
                </button>
              </li>
            );
          })}
        </ul>
      )}

      {(results || playlists || playlist) && (
        <button className="add__file" type="button" onClick={clearLists}>
          effacer les résultats
        </button>
      )}
    </section>
  );
}
